/** Imports */
import {type Platform} from '@/mongoSchemas/Page/Pages.zod';
import {type PostState} from '@/mongoSchemas/Post/Posts.zod';


/** Types */
export type EditablePostField = 'caption' | 'description' | 'hashTags' | 'firstComment' | 'schedule';
export type PostFieldEditability =
    | 'editable'
    | 'immutable-state'
    | 'pending-only'
    | 'unsupported'
    | 'story-unsupported'
    | 'platform-edit-window-expired';
type PostFieldEditabilityInput = Readonly<{
    platform: Platform;
    state: PostState;
    field: EditablePostField;
    postingType?: string;
    hasPublishedFirstComment?: boolean;
    scheduledAt: Date | string;
    now?: Date;
}>;


/** Constants */
export const TWITTER_POST_EDIT_WINDOW_MS = 30 * 60 * 1_000;
export const YOUTUBE_TAGS_MAX_LENGTH = 500;
const LOCAL_EDITABLE_STATES = new Set<PostState>(['draft', 'pending']);
const IMMUTABLE_STATES = new Set<PostState>(['inPosting', 'failed', 'deleted']);


/** Helpers */
export function getPostFieldEditability(input: PostFieldEditabilityInput): PostFieldEditability {
    const {
        platform, state, field, postingType, hasPublishedFirstComment, scheduledAt, now = new Date(),
    } = input;
    if (IMMUTABLE_STATES.has(state)) return 'immutable-state';

    if (LOCAL_EDITABLE_STATES.has(state)) {
        if (
            ((field === 'caption') || (field === 'description'))
            && ((platform === 'facebook') || (platform === 'instagram'))
            && (postingType === 'story')
        ) return 'story-unsupported';
        return 'editable';
    }

    if (state !== 'posted') return 'pending-only';

    switch (field) {
        case 'caption':
        case 'description':
            if ((platform === 'facebook') && (postingType !== 'story')) return 'editable';
            if (platform === 'youtube') return 'editable';
            if (platform === 'twitter') {
                const scheduledTime = new Date(scheduledAt).getTime();
                if (
                    !Number.isNaN(scheduledTime)
                    && (now.getTime() < (scheduledTime + TWITTER_POST_EDIT_WINDOW_MS))
                ) return 'editable';
                return 'platform-edit-window-expired';
            }
            return 'unsupported';
        case 'hashTags':
            return (platform === 'youtube') ? 'editable' : 'pending-only';
        case 'firstComment':
            if (
                (platform === 'facebook')
                && (postingType !== 'story')
                && Boolean(hasPublishedFirstComment)
            ) return 'editable';
            return 'pending-only';
        case 'schedule':
            return 'pending-only';
    }
}

/** YouTube counts separators and the quotes required around tags containing spaces. */
export function getYouTubeTagCharacterCount(tags: Array<string>): number {
    return tags.reduce((total, tag, index) => {
        const separatorLength = (index === 0) ? 0 : 1;
        const quoteLength = (tag.includes(' ')) ? 2 : 0;
        return total + separatorLength + quoteLength + tag.length;
    }, 0);
}
